import { fg } from '@atlaskit/platform-feature-flags';
import { addUFOCustomData } from '@atlaskit/react-ufo/custom-data';
import {
	addHoldByID,
	getActiveInteraction,
	removeHoldByID,
	tryComplete,
} from '@atlaskit/react-ufo/interaction-metrics';
import UFORouteName from '@atlaskit/react-ufo/route-name-context';
import traceUFOTransition from '@atlaskit/react-ufo/trace-transition';

/**
 * Re-fire the page-load VC90 UFO metric as a `transition` interaction (initial=false) when a
 * user performs a subsequent search on an already-loaded search page (query resubmit or
 * filter change).
 *
 * The Confluence `ufo-search-page-ignoring-smart-answers` variant does NOT need to be
 * handled here: react-ufo derives it automatically. When any interaction whose `ufoName`
 * matches `config.extraSearchPageInteraction.searchPageMetricName` finishes, react-ufo's
 * `createExtraSearchPageInteractionPayload` clones it (excluding SAIN holds) and emits the
 * `-ignoring-smart-answers` twin — for transitions just as for page loads. Since our
 * transition reuses the primary `searchPageMetricName` (== `UFORouteName.current`), the
 * SAIN-ignoring transition twin is emitted for free.
 *
 * Why an imperative hold (rather than relying on the loading skeleton's hold):
 *
 * We want VC90 to measure from the moment the user starts the search (Enter / filter click)
 * until the new results are rendered — i.e. it must include URL sync + network + render. If
 * we instead started the transition when the loading skeleton mounts, we would miss the time
 * between the gesture and the skeleton commit (and, on a Relay cache hit, the skeleton may
 * not even suspend). So we start the transition synchronously at the gesture and immediately
 * take an imperative hold on the freshly-created interaction so it cannot settle during
 * navigation/network. The hold is released when the results finish rendering
 * (`releaseReSearchTransitionHold`, driven by `resultsState` reaching a terminal value),
 * which is exactly the VC90 completion point.
 */

// The interaction id currently held open by a re-search transition, or null when none is in
// flight. Module-level so the fire (input / filter gesture) and the release (main.tsx
// effect) can coordinate without extra React state.
let heldReSearchInteractionId: string | null = null;

const RE_SEARCH_TRANSITION_HOLD_NAME = 'search-page-re-search';
const RE_SEARCH_TRANSITION_HOLD_ID = 'search-page-re-search-hold';

/**
 * Start a re-search VC90 transition and hold it open until the results render.
 *
 * Fires `traceUFOTransition` for the current search page route and immediately takes an
 * imperative hold on the new interaction so it measures the full gesture→results duration.
 * No-op when the gate is off or no search page route is active.
 *
 * If a previous re-search transition is still in flight (e.g. the user searches again before
 * the first completes), its hold is released first so the old interaction can settle and the
 * new one starts cleanly.
 */
export function startReSearchTransition(): void {
	if (!fg('enable_same_page_re_search_vc90')) {
		return;
	}

	const ufoName = UFORouteName.current;
	if (!ufoName) {
		return;
	}

	// A rapid second search: release the previous hold so the first interaction can settle
	// before we abort it and start a new transition.
	if (heldReSearchInteractionId !== null) {
		releaseReSearchTransitionHold();
	}

	// Starts a new `transition` interaction and sets it as the active interaction.
	traceUFOTransition(ufoName);

	// Only hold a genuinely-new transition (traceUFOTransition may no-op on a sampling
	// coinflip miss, leaving the previous interaction active).
	const interaction = getActiveInteraction();
	if (!interaction || interaction.type !== 'transition') {
		return;
	}

	// Tag the interaction so this manually-fired re-search transition can be distinguished
	// from normal (router-driven) page-load / transition events in analytics. Surfaces on the
	// emitted UFO event under `customData` and is dropped when the interaction completes, so it
	// cannot leak into other VC90 events. Targets the interaction just created above.
	addUFOCustomData({ reSearch: true });

	addHoldByID(
		interaction.id,
		[{ name: RE_SEARCH_TRANSITION_HOLD_NAME }],
		RE_SEARCH_TRANSITION_HOLD_NAME,
		RE_SEARCH_TRANSITION_HOLD_ID,
	);
	heldReSearchInteractionId = interaction.id;
}

/**
 * Release the hold taken by `startReSearchTransition`, allowing the transition to complete at
 * the correct VC90 point. Called when the results reach a terminal state. No-op when no
 * re-search transition is in flight.
 *
 * Why the double `requestAnimationFrame` before `tryComplete`:
 *
 * The results terminal state (`resultsState` → RESULTS / NO_RESULTS / ERROR) is a React state
 * signal that fires inside a `useEffect` — i.e. *after* React has committed the new fragment
 * data to the tree, but *before* the browser has painted those DOM nodes. If we called
 * `tryComplete` synchronously here, it would stamp `interaction.end` at the pre-paint commit
 * moment, truncating VC90 to the commit time (observed as e.g. ~235ms) even though the real
 * visually-complete paint — which TTI/FMP and the VC observer measure — lands much later
 * (e.g. ~1742ms). That produced the "VC90 far lower than TTI/FMP" bug on fast re-searches.
 *
 * To finalise at the true paint boundary we mirror how react-ufo's own segment `complete()`
 * defers completion: schedule across two animation frames so the callback runs after the
 * results have actually painted, then remove our hold and complete. This lets the per-
 * interaction VC observer record the final (results) revision before the interaction settles,
 * bringing VC90 back in line with TTI/FMP.
 *
 * Why we only use rAF while the tab is visible:
 *
 * Browsers pause/throttle `requestAnimationFrame` callbacks in hidden/background tabs. If the
 * results resolve while the tab is hidden, an rAF-deferred `finalise` would not run until the
 * tab is foregrounded again — by which point `interaction.end` would be stamped far too late
 * (inflating VC90), or UFO would have already aborted the interaction (`page_unloaded` /
 * timeout) and dropped the metric. So when the page is not visible we complete synchronously
 * instead. This mirrors react-ufo's own segment `complete()` path, which likewise checks
 * `document.visibilityState === 'visible'` before choosing rAF vs a synchronous `tryComplete`
 * (see `react-ufo/atlaskit/src/segment/segment.tsx`). The more complete primitive for this is
 * react-ufo's `scheduleOnPaint` (`.../segment/schedule-on-paint.ts`), which additionally does a
 * `setTimeout(cb, 100)` fallback for hidden tabs and a `scheduler.postTask` fast-path; it is
 * not yet exported from a public `@atlaskit/react-ufo` entrypoint. TODO: switch to the shared
 * `scheduleOnPaint` util once it is exported, and drop this inline scheduling.
 *
 * Falls back to a synchronous completion when `requestAnimationFrame` is unavailable (e.g.
 * non-DOM/test environments).
 */
export function releaseReSearchTransitionHold(): void {
	if (heldReSearchInteractionId === null) {
		return;
	}

	const interactionId = heldReSearchInteractionId;
	heldReSearchInteractionId = null;

	const finalise = () => {
		removeHoldByID(interactionId, RE_SEARCH_TRANSITION_HOLD_ID);
		// With our hold gone the interaction can complete once its other holds (results
		// segment / loading skeleton) have cleared.
		tryComplete(interactionId);
	};

	const isPageVisible = globalThis?.document?.visibilityState === 'visible';
	const canUseRaf = typeof requestAnimationFrame !== 'undefined';

	if (isPageVisible && canUseRaf) {
		// Defer to after the browser paints the newly-rendered results so VC90 is stamped at
		// the real visually-complete point rather than the pre-paint React-commit point.
		requestAnimationFrame(() => {
			requestAnimationFrame(finalise);
		});
	} else {
		// Hidden tab (rAF is paused) or no DOM: complete synchronously so the interaction is
		// not left dangling until the tab is foregrounded.
		finalise();
	}
}
